import api from './api';
import type { AxiosRequestConfig } from 'axios';
import { AxiosError } from 'axios';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface RequestQueueItem<T = unknown> {
  id: string;
  request: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  priority: number;
  retryCount: number;
}

/**
 * Enhanced API Service with caching and request queuing to prevent 429 errors
 * Provides transparent optimization while maintaining existing API interfaces
 */
class EnhancedApiService {
  private cache = new Map<string, CacheEntry<unknown>>();
  private requestQueue: RequestQueueItem<unknown>[] = [];
  private processing = false;
  private activeRequests = 0;
  private readonly MAX_CONCURRENT = 2; // More aggressive rate limiting
  private readonly REQUEST_DELAY = 500; // Increased delay to 500ms between requests
  private readonly MAX_RETRIES = 3;
  
  // Request deduplication - track ongoing requests to prevent duplicates
  private ongoingRequests = new Map<string, Promise<unknown>>();

  /**
   * Cached GET request with automatic request queuing
   * @param url - API endpoint URL
   * @param config - Axios config object
   * @param ttl - Time to live in milliseconds (default: 5 minutes)
   * @param priority - Request priority (higher = processed first)
   */
  async cachedGet<T>(
    url: string,
    config?: AxiosRequestConfig,
    ttl: number = 5 * 60 * 1000, // 5 minutes default
    priority: number = 0
  ): Promise<T> {
    const cacheKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < cached.ttl) {
      console.log(`🎯 ENHANCED API - Cache HIT: ${cacheKey}`);
      return cached.data as T;
    }

    // Check for ongoing identical requests (deduplication)
    const ongoingRequest = this.ongoingRequests.get(cacheKey);
    if (ongoingRequest) {
      console.log(`🔄 ENHANCED API - Request DEDUPLICATION: ${cacheKey} - reusing ongoing request`);
      return ongoingRequest as Promise<T>;
    }

    console.log(`📡 ENHANCED API - Cache MISS: ${cacheKey} - queuing request`);
    console.log(`🔄 ENHANCED API - Queue stats: ${this.requestQueue.length} queued, ${this.activeRequests} active`);

    // Create and track the request promise for deduplication
    const requestPromise = new Promise<T>((resolve, reject) => {
      const queueItem: RequestQueueItem<T> = {
        id: Math.random().toString(36),
        request: async () => {
          console.log(`🚀 ENHANCED API - Executing queued request: ${url}`);
          const response = await api.get(url, {
            ...config,
            timeout: config?.timeout || 30000 // 30 second timeout
          });

          // Cache the result
          this.cache.set(cacheKey, {
            data: response.data,
            timestamp: Date.now(),
            ttl
          });

          console.log(`💾 ENHANCED API - Cache SET: ${cacheKey} (TTL: ${ttl}ms)`);
          return response.data;
          // No finally-delete here — the dedup key must survive retries,
          // otherwise a duplicate request races the retrying original during backoff.
        },
        resolve: (value: T) => {
          this.ongoingRequests.delete(cacheKey);
          resolve(value);
        },
        reject: (error: Error) => {
          this.ongoingRequests.delete(cacheKey);
          reject(error);
        },
        priority,
        retryCount: 0
      };

      this.requestQueue.push(queueItem as RequestQueueItem<unknown>);
      this.processQueue();
    });

    // Track this request for deduplication
    this.ongoingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * Direct POST request without caching (for mutations)
   * Still uses request queuing to prevent rate limiting
   */
  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    priority: number = 1 // Higher priority for mutations
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const queueItem: RequestQueueItem<T> = {
        id: Math.random().toString(36),
        request: async () => {
          console.log(`🚀 ENHANCED API - Executing queued POST request: ${url}`);
          const response = await api.post(url, data, {
            ...config,
            timeout: config?.timeout || 30000
          });
          return response.data;
        },
        resolve,
        reject: (error: Error) => reject(error),
        priority,
        retryCount: 0
      };

      this.requestQueue.push(queueItem as RequestQueueItem<unknown>);
      this.processQueue();
    });
  }

  /**
   * Process the request queue with rate limiting and retry logic
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.activeRequests >= this.MAX_CONCURRENT) {
      return;
    }

    this.processing = true;

    while (this.requestQueue.length > 0 && this.activeRequests < this.MAX_CONCURRENT) {
      // Sort by priority (higher priority first)
      this.requestQueue.sort((a, b) => b.priority - a.priority);
      const item = this.requestQueue.shift()!;

      this.activeRequests++;
      
      try {
        const result = await item.request();
        item.resolve(result);
        console.log(`✅ ENHANCED API - Request completed successfully: ${item.id}`);
      } catch (error: unknown) {
        console.error(`❌ ENHANCED API - Request failed: ${item.id}`, error);
        
        // Enhanced retry logic with exponential backoff and special 429 handling
        if (item.retryCount < this.MAX_RETRIES && this.shouldRetry(error)) {
          item.retryCount++;
          
          // Special handling for 429 errors - longer delays
          let delay: number;
          if (error instanceof AxiosError && error.response?.status === 429) {
            // For 429 errors, use longer exponential backoff: 2s, 8s, 32s
            delay = Math.pow(4, item.retryCount) * 500;
            console.log(`🔄 ENHANCED API - 429 Retry ${item.id} in ${delay}ms (attempt ${item.retryCount}/${this.MAX_RETRIES})`);
          } else {
            // Standard exponential backoff for other errors: 2s, 4s, 8s
            delay = Math.pow(2, item.retryCount) * 1000;
            console.log(`🔄 ENHANCED API - Retrying request ${item.id} in ${delay}ms (attempt ${item.retryCount}/${this.MAX_RETRIES})`);
          }
          
          setTimeout(() => {
            this.requestQueue.unshift(item); // Add back to front
            this.processQueue();
          }, delay);
        } else {
          console.error(`💥 ENHANCED API - Request failed permanently: ${item.id}`, error);
          item.reject(error instanceof Error ? error : new Error(String(error)));
        }
      } finally {
        this.activeRequests--;
      }

      // Add delay between requests to prevent rate limiting
      if (this.requestQueue.length > 0) {
        await this.delay(this.REQUEST_DELAY);
      }
    }

    this.processing = false;

    // Continue processing if there are more items
    if (this.requestQueue.length > 0) {
      setTimeout(() => this.processQueue(), this.REQUEST_DELAY);
    }
  }

  /**
   * Determine if an error should trigger a retry
   */
  private shouldRetry(error: unknown): boolean {
    if (error instanceof AxiosError) {
      // Timeouts are safe to retry
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;

      // No response means either a network error OR a CORS block — both are
      // indistinguishable here, and CORS failures are deterministic (retrying
      // never recovers, it just floods the console). Don't retry either.
      if (!error.response) {
        console.warn('No response (network/CORS). Not retrying.');
        return false;
      }

      const status = error.response.status;

      // Enhanced 429 handling with longer delays
      if (status === 429) {
        console.warn(`⚠️ ENHANCED API - 429 Rate Limit Hit - will retry with exponential backoff`);
        return true;
      }

      return status >= 500; // Server errors
    }

    return false; // Don't retry non-Axios errors
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Cache management methods
   */
  
  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🧹 Cache cleared');
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  invalidateCache(pattern: string): void {
    let invalidatedCount = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        invalidatedCount++;
      }
    }
    console.log(`🗑️ Invalidated ${invalidatedCount} cache entries matching: ${pattern}`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }

  /**
   * Get queue statistics
   */
  getQueueStats(): { queueLength: number; activeRequests: number; processing: boolean } {
    return {
      queueLength: this.requestQueue.length,
      activeRequests: this.activeRequests,
      processing: this.processing
    };
  }

  /**
   * Cleanup expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} expired cache entries`);
    }
  }

  constructor() {
    // Cleanup cache every 5 minutes
    setInterval(() => this.cleanupCache(), 5 * 60 * 1000);
    
    console.log('🚀 ENHANCED API SERVICE INITIALIZED - Caching, request queuing, and deduplication active');
    console.log(`⚙️ ENHANCED API CONFIG - Max concurrent: ${this.MAX_CONCURRENT}, Delay: ${this.REQUEST_DELAY}ms, Max retries: ${this.MAX_RETRIES}`);
  }
}

// Export singleton instance
export const enhancedApi = new EnhancedApiService();

// Export class for testing
export { EnhancedApiService };